import { ColDef } from 'ag-grid-community';
import { t } from 'i18next';
import { isObject, upperFirst } from 'lodash';

import { LineTypeCell, PartAliasCell } from 'bom/elements';
import { BomPartCellHelper } from 'bom/helpers';
import { TextLine } from 'core/elements';
import { AgGridFilterOption } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { IPartBom, IPartBomItem, IPartBomItemNote } from 'partBoms/types';
import { ImageCell } from 'parts/elements';
import { PartNumberHelper } from 'parts/helpers';

import { ActionsCell, CostCell, ExtCostCell, NotesCell, PartNumberCell } from './cells';

export const getColumnDefs = (partBom: IPartBom, isAlternateVersionEnabled: boolean): ColDef[] => {
  const alternateVendorPartNumberColumn = isAlternateVersionEnabled
    ? {
        colId: 'alternateVendorPartNumber',
        headerName: t('labelAlternateVendorPartNumber'),
        field: 'alternateVendorPartNumber',
        filter: 'agTextColumnFilter',
      }
    : null;

  return [
    {
      colId: 'line',
      headerName: '#',
      pinned: 'left',
      field: 'line',
      wrapText: false,
      suppressPaste: true,
      suppressFillHandle: true,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      resizable: false,
      minWidth: 80,
      width: 80,
      maxWidth: 80,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      lockPinned: true,
      enableValue: true,
      enablePivot: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: false,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      cellStyle: { textAlign: 'center' },
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { placeholder: '' },
    },
    {
      colId: 'actions',
      width: 80,
      maxWidth: 80,
      sortable: false,
      resizable: false,
      enableRowGroup: false,
      suppressColumnsToolPanel: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: ActionsCell,
      cellRendererParams: {
        partBom,
      },
    },
    {
      colId: 'image',
      headerName: t('headerImage'),
      field: 'partArr.defaultImage',
      sortable: false,
      resizable: false,
      enableRowGroup: false,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      filter: false,
      suppressPaste: true,
      suppressFillHandle: true,
      pinned: 'left',
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      maxWidth: 74,
      valueFormatter: ({ value }) => value?.alt,
      cellRenderer: ImageCell,
    },
    {
      colId: 'number',
      headerName: t('headerPartNumber'),
      filter: 'agTextColumnFilter',
      field: 'partArr.number',
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 160,
      maxWidth: 400,
      enableValue: true,
      enablePivot: true,
      cellStyle: { alignItems: 'flex-start' },
      valueFormatter: ({ value }) => PartNumberHelper.toLabel(value),
      filterValueGetter: ({ data }) => {
        if (!data) {
          return '';
        }

        const { partArr, vendorPartNumber, alternateVendorPartNumber } = data as IPartBomItem;

        return `${partArr?.number} ${vendorPartNumber} ${alternateVendorPartNumber}`;
      },
      cellRenderer: PartNumberCell,
    },
    alternateVendorPartNumberColumn,
    {
      colId: 'manufacturer',
      headerName: t('headerManufacturer'),
      filter: 'agSetColumnFilter',
      field: 'partArr.manufacturerName',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 120,
      width: 160,
      maxWidth: 190,
      cellRendererParams: {
        textEllipsis: false,
      },
    },
    {
      colId: 'description',
      headerName: t('headerDescription'),
      autoHeight: true,
      field: 'partArr.descriptionLong',
      filter: 'agTextColumnFilter',
      minWidth: 200,
      width: 355,
      maxWidth: 900,
      cellRenderer: ({ value }) => <TextLine text={value} maxRows={4} textEllipsis />,
    },
    {
      colId: 'quantity',
      headerName: t('headerQuantity'),
      field: 'quantity',
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sumQty',
      minWidth: 95,
      width: 110,
      cellStyle: { textAlign: 'right' },
      editable: true,
      valueFormatter: ({ value }) => NumberHelper.format({ amount: value }),
    },
    {
      colId: 'uom',
      headerName: t('headerUom'),
      field: 'uom',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      minWidth: 75,
      width: 100,
      suppressPaste: true,
      suppressFillHandle: true,
      editable: true,
      cellEditorSelector: ({ data }) => ({
        component: 'agRichSelectCellEditor',
        params: {
          values: data?.allowedUoms,
        },
      }),
    },
    {
      colId: 'notes',
      headerName: t('headerNotes'),
      field: 'notes',
      sortable: true,
      minWidth: 400,
      width: 400,
      maxWidth: 600,
      filter: 'agTextColumnFilter',
      filterParams: {
        textFormatter: formatNotes,
      },
      valueFormatter: ({ value }) => formatNotes(value),
      cellRenderer: NotesCell,
      cellRendererParams: { isRowEditable: true },
    },
    {
      colId: 'purchaseCost',
      headerName: t('headerUnitCost'),
      field: 'purchaseCost',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      cellClass: 'paddingSmallLeft',
      minWidth: 140,
      maxWidth: 220,
      cellRenderer: CostCell,
    },
    {
      colId: 'extPurchaseCost',
      headerName: t('headerExtPurchaseCost'),
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => data && (data.purchaseCost || 0) * (data.quantity || 0),
      cellRenderer: ExtCostCell,
    },
    {
      colId: 'extStandardCost',
      headerName: t('headerExtStandardCost'),
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => data && (data.cost || 0) * (data.quantity || 0),
      cellRenderer: ExtCostCell,
    },
    {
      colId: 'lineType',
      headerName: t('headerLineType'),
      sortable: false,
      suppressPaste: true,
      suppressFillHandle: true,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall noDecorationCell',
      minWidth: 100,
      width: 100,
      maxWidth: 100,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => t(`labelLineType${upperFirst(value)}`),
      },
      valueGetter: ({ data }) => BomPartCellHelper.getLineType(data),
      cellRenderer: LineTypeCell,
      cellStyle: { textAlign: 'center' },
    },
    {
      colId: 'alias',
      headerName: t('headerAlias'),
      filter: 'agTextColumnFilter',
      suppressPaste: true,
      suppressFillHandle: true,
      valueGetter: ({ data }) => BomPartCellHelper.getAlias(data),
      cellRenderer: PartAliasCell,
      minWidth: 150,
      maxWidth: 200,
    },
  ].filter(Boolean) as ColDef[];
};

const formatNotes = (data: Record<string, IPartBomItemNote>) => {
  if (!data) {
    return '';
  }

  return isObject(data)
    ? Object.values(data)
        .map((note: IPartBomItemNote) => note.content)
        .join(' ')
        .toLowerCase()
    : data;
};
